import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, CheckCircle, Circle, PlayCircle,
  Clock, User, BarChart3, Award, FileText, Loader2, BookOpen,
  CheckSquare, Target, Lightbulb, AlertCircle, GraduationCap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import VideoPlayer from "@/components/learning/VideoPlayer";
import CurriculumSidebar from "@/components/learning/CurriculumSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function CourseViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [completedLectures, setCompletedLectures] = useState([]);
  const [activeTab, setActiveTab] = useState("content");
  const [quizResult, setQuizResult] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizReview, setQuizReview] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const loadCourse = useCallback(async () => {
    setLoading(true);
    try {
      const [courseRes, enrollRes, resultsRes] = await Promise.all([
        axios.get(`${API}/courses/${id}`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/enrollments`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/courses/${id}/results`, { headers: getHeaders(), withCredentials: true }),
      ]);

      const courseData = courseRes.data;
      setCourse(courseData);

      const enrollList = Array.isArray(enrollRes.data) ? enrollRes.data : enrollRes.data.enrollments || [];
      const myEnroll = enrollList.find((e) => e.course_id === id);
      setEnrollment(myEnroll);

      const saved = localStorage.getItem(`course_${id}_completed`);
      if (saved) setCompletedLectures(JSON.parse(saved));

      const results = Array.isArray(resultsRes.data) ? resultsRes.data : resultsRes.data.results || [];
      const latestResults = {};
      results.forEach((r) => { if (r.type === "quiz") latestResults[`week_${r.week}`] = r; });
      setQuizResult(latestResults);

      if (courseData.modules?.length > 0) {
        setCurrentWeek(courseData.modules[0].week);
      }
    } catch {
      toast.error("Failed to load course");
      navigate("/learning");
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  const saveCompleted = (lectures) => {
    localStorage.setItem(`course_${id}_completed`, JSON.stringify(lectures));
  };

  // Course overview metrics
  const modules = course?.modules || [];
  const totalLectures = modules.reduce((sum, m) => sum + (m.lessons?.length || 0), 0);
  const progressPct = totalLectures > 0 ? Math.round((completedLectures.length / totalLectures) * 100) : 0;

  // If no week selected yet, show course overview
  if (currentWeek == null) {
    return <CourseOverview
      course={course}
      loading={loading}
      modules={modules}
      totalLectures={totalLectures}
      completedLectures={completedLectures}
      progressPct={progressPct}
      onStart={() => {
        if (modules.length > 0) setCurrentWeek(modules[0].week);
      }}
      onBack={() => navigate("/learning")}
    />;
  }

  const currentModule = modules.find((m) => m.week === currentWeek);
  const currentLessonData = currentModule?.lessons?.[currentLesson];
  const lectureKey = currentLessonData ? `${currentWeek}-${currentLessonData.title}` : "";
  const isCompleted = completedLectures.includes(lectureKey);
  const hasVideo = !!currentLessonData?.video_url;
  const hasContent = !!currentLessonData?.content;
  const moduleQuiz = currentModule?.quiz;
  const weekQuizResult = quizResult?.[`week_${currentWeek}`];

  // Quiz handling
  const handleQuizAnswer = (qIdx, answer) => {
    setQuizAnswers((prev) => ({ ...prev, [qIdx]: answer }));
  };

  const handleSubmitQuiz = async () => {
    if (!course || currentWeek == null) return;
    setQuizLoading(true);
    try {
      const quiz = currentModule?.quiz;
      if (!quiz) return;
      const res = await axios.post(
        `${API}/courses/${id}/quiz/${currentWeek}/submit`,
        { answers: quizAnswers, time_taken_seconds: 0 },
        { headers: getHeaders(), withCredentials: true }
      );
      setQuizSubmitted(true);
      setQuizResult((prev) => ({ ...prev, [`week_${currentWeek}`]: res.data }));
      toast.success(res.data.passed ? "Quiz passed!" : "Quiz completed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit quiz");
    }
    setQuizLoading(false);
  };

  // Navigation
  const handleSelectLecture = (week, lessonIdx) => {
    setCurrentWeek(week);
    setCurrentLesson(lessonIdx);
    setQuizSubmitted(false);
    setQuizAnswers({});
    setQuizReview(false);
    setActiveTab("content");
  };

  const handleMarkComplete = async () => {
    if (!currentLessonData) return;
    if (isCompleted) {
      const updated = completedLectures.filter((l) => l !== lectureKey);
      setCompletedLectures(updated);
      saveCompleted(updated);
      return;
    }
    const updated = [...completedLectures, lectureKey];
    setCompletedLectures(updated);
    saveCompleted(updated);

    const completedWeeks = modules
      .filter((m) => (m.lessons || []).every((l) => updated.includes(`${m.week}-${l.title}`)))
      .map((m) => m.week);

    try {
      await axios.put(`${API}/courses/${id}/progress`, {
        progress: Math.round((updated.length / totalLectures) * 100),
        completed_weeks: completedWeeks,
      }, { headers: getHeaders(), withCredentials: true });
    } catch {}
  };

  const handleNext = () => {
    if (!currentModule) return;
    const lessons = currentModule.lessons || [];
    if (currentLesson < lessons.length - 1) {
      setCurrentLesson(currentLesson + 1);
    } else {
      const idx = modules.findIndex((m) => m.week === currentWeek);
      if (idx < modules.length - 1) {
        setCurrentWeek(modules[idx + 1].week);
        setCurrentLesson(0);
      }
    }
    setQuizSubmitted(false);
    setQuizAnswers({});
    setQuizReview(false);
    setActiveTab("content");
  };

  const handlePrev = () => {
    if (!currentModule) return;
    if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
    } else {
      const idx = modules.findIndex((m) => m.week === currentWeek);
      if (idx > 0) {
        setCurrentWeek(modules[idx - 1].week);
        setCurrentLesson((modules[idx - 1].lessons || []).length - 1);
      }
    }
    setQuizSubmitted(false);
    setQuizAnswers({});
    setQuizReview(false);
    setActiveTab("content");
  };

  return (
    <div className="h-screen flex flex-col bg-slate-900">
      {/* Top Nav */}
      <header className="bg-slate-900 text-white px-4 py-2 flex items-center justify-between z-10 border-b border-slate-700">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/learning")} className="text-slate-400 hover:text-white p-1 flex-shrink-0">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 hidden sm:block">
            <p className="text-sm font-medium truncate max-w-md">{course?.title}</p>
            <p className="text-[10px] text-slate-400 truncate">
              {currentModule?.title} &middot; {currentLessonData?.title}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-slate-400 hidden sm:block">{completedLectures.length}/{totalLectures}</span>
          <Progress value={progressPct} className="w-20 h-1.5 hidden sm:block" />
          <button onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-[11px] text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 px-2.5 py-1 rounded">
            {sidebarOpen ? "Hide" : "Curriculum"}
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Main Content */}
        <div className={`flex-1 overflow-y-auto bg-white ${sidebarOpen ? "" : ""}`}>
          {/* Video Player */}
          {hasVideo && (
            <div className="w-full bg-slate-900">
              <div className="max-w-5xl mx-auto">
                <VideoPlayer
                  url={currentLessonData.video_url}
                  type={currentLessonData.video_type || "youtube"}
                  title={currentLessonData.title}
                  onComplete={() => { if (!isCompleted) handleMarkComplete(); }}
                />
              </div>
            </div>
          )}

          {/* Lesson Body */}
          <div className="max-w-4xl mx-auto p-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] text-slate-400 mb-3">
              <span className="truncate max-w-[120px]">{course?.title}</span>
              <ChevronRight className="w-2.5 h-2.5 flex-shrink-0" />
              <span className="truncate max-w-[120px]">{currentModule?.title}</span>
              <ChevronRight className="w-2.5 h-2.5 flex-shrink-0" />
              <span className="text-slate-600 font-medium truncate">{currentLessonData?.title}</span>
            </div>

            <h2 className="text-xl font-bold text-slate-800 mb-1">{currentLessonData?.title}</h2>
            {currentLessonData?.duration_minutes && (
              <p className="text-xs text-slate-400 mb-4 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {currentLessonData.duration_minutes} min
              </p>
            )}

            {/* Objectives */}
            {currentLessonData?.objectives?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 p-3 mb-4 rounded">
                <p className="text-[11px] font-semibold text-amber-800 mb-1.5 flex items-center gap-1">
                  <Target className="w-3 h-3" /> Learning Objectives
                </p>
                <ul className="space-y-0.5">
                  {currentLessonData.objectives.map((obj, i) => (
                    <li key={i} className="text-[11px] text-amber-700 flex items-start gap-1.5">
                      <CheckSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      {obj}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Text content (when no video) */}
            {hasContent && !hasVideo && (
              <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed mb-6">
                {currentLessonData.content}
              </div>
            )}

            {/* Video + text description */}
            {hasVideo && currentLessonData?.description && (
              <div className="text-xs text-slate-600 bg-app p-3 rounded mb-6">
                {currentLessonData.description}
              </div>
            )}

            {/* Quiz / Exercises / Culture tabs */}
            {currentModule && (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                <TabsList className="rounded-none border border-slate-200 bg-white">
                  {moduleQuiz && (
                    <TabsTrigger value="quiz" className="text-xs rounded-none data-[state=active]:bg-amber-50">
                      <FileText className="w-3.5 h-3.5 mr-1" /> Quiz
                    </TabsTrigger>
                  )}
                  {currentModule.practical_exercises?.length > 0 && (
                    <TabsTrigger value="exercises" className="text-xs rounded-none data-[state=active]:bg-amber-50">
                      <Lightbulb className="w-3.5 h-3.5 mr-1" /> Exercises
                    </TabsTrigger>
                  )}
                  {currentModule.cultural_spotlight && (
                    <TabsTrigger value="culture" className="text-xs rounded-none data-[state=active]:bg-amber-50">
                      <BookOpen className="w-3.5 h-3.5 mr-1" /> Culture
                    </TabsTrigger>
                  )}
                </TabsList>

                {moduleQuiz && (
                  <TabsContent value="quiz">
                    <div className="bg-white border border-slate-200 p-4">
                      {weekQuizResult && quizSubmitted ? (
                        <div>
                          <div className={`p-4 mb-4 border ${weekQuizResult.passed
                            ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                            : "bg-red-50 border-red-200 text-red-800"}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                                weekQuizResult.passed ? "bg-emerald-200 text-emerald-700" : "bg-red-200 text-red-700"
                              }`}>{weekQuizResult.score}%</div>
                              <div>
                                <p className="text-sm font-semibold">{weekQuizResult.passed ? "Passed!" : "Keep studying"}</p>
                                <p className="text-[11px] opacity-75">{weekQuizResult.correct}/{weekQuizResult.total} correct</p>
                              </div>
                            </div>
                          </div>
                          <Button onClick={() => setQuizReview(!quizReview)} variant="outline"
                            className="h-7 text-[10px] rounded-none mb-3">
                            {quizReview ? "Hide" : "Review"} Answers
                          </Button>
                          {quizReview && moduleQuiz.questions?.map((q, qi) => {
                            const result = weekQuizResult.results?.find((r) => r.question_index === qi);
                            return (
                              <div key={qi} className={`p-2.5 mb-2 border text-xs ${
                                result?.is_correct ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                                <p className="font-medium text-slate-700 mb-0.5">{q.question}</p>
                                <p className="text-[10px] text-slate-500">
                                  Your answer: <span className={result?.is_correct ? "text-emerald-600" : "text-red-600"}>
                                    {result?.user_answer || "—"}</span>
                                </p>
                                {!result?.is_correct && <p className="text-[10px] text-emerald-600">Correct: {q.correct_answer}</p>}
                                <p className="text-[9px] text-slate-400 mt-0.5">{q.explanation}</p>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div>
                          <div className="mb-3">
                            <h4 className="text-sm font-semibold text-slate-700">{moduleQuiz.title || "Quiz"}</h4>
                            <p className="text-[10px] text-slate-400">
                              {moduleQuiz.num_questions} questions · Pass: {moduleQuiz.passing_score || 70}%
                              {moduleQuiz.time_limit_minutes ? ` · ${moduleQuiz.time_limit_minutes} min` : ""}
                            </p>
                          </div>
                          <div className="space-y-3">
                            {moduleQuiz.questions?.map((q, qi) => (
                              <div key={qi} className="p-3 border border-slate-200">
                                <p className="text-xs font-medium text-slate-700 mb-2">{qi + 1}. {q.question}</p>
                                <div className="space-y-1">
                                  {q.options?.map((opt, oi) => (
                                    <label key={oi}
                                      className={`flex items-center gap-2 p-2 border text-xs cursor-pointer ${
                                        quizAnswers[qi] === opt ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:bg-app"
                                      }`}>
                                      <input type="radio" name={`q_${qi}`} value={opt}
                                        checked={quizAnswers[qi] === opt}
                                        onChange={() => handleQuizAnswer(qi, opt)}
                                        className="w-3.5 h-3.5 text-amber-500" />
                                      {opt}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                          <Button onClick={handleSubmitQuiz} disabled={quizLoading}
                            className="mt-3 h-8 text-xs rounded-none bg-amber-600 hover:bg-amber-500">
                            {quizLoading && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                            Submit Quiz
                          </Button>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                )}

                {currentModule.practical_exercises?.length > 0 && (
                  <TabsContent value="exercises">
                    <div className="space-y-3">
                      {currentModule.practical_exercises.map((ex, i) => (
                        <div key={i} className="bg-white border border-slate-200 p-3">
                          <div className="flex items-center gap-2 mb-1.5">
                            <Badge className="text-[8px] px-1 py-0 bg-purple-100 text-purple-700 border-none">{ex.exercise_type}</Badge>
                            {ex.difficulty && (
                              <Badge className={`text-[8px] px-1 py-0 border-none ${
                                ex.difficulty === "easy" ? "bg-emerald-100 text-emerald-700"
                                  : ex.difficulty === "medium" ? "bg-amber-100 text-amber-700"
                                  : "bg-red-100 text-red-700"}`}>
                                {ex.difficulty}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs font-medium text-slate-700 mb-0.5">{ex.scenario}</p>
                          <p className="text-[11px] text-slate-500 mb-1.5">{ex.task}</p>
                          {ex.hint && (
                            <details className="text-[10px] text-slate-400 cursor-pointer mb-0.5">
                              <summary className="hover:text-slate-600">Hint</summary>
                              <p className="mt-1 p-2 bg-app rounded">{ex.hint}</p>
                            </details>
                          )}
                          {ex.expected_output && (
                            <details className="text-[10px] text-slate-400 cursor-pointer">
                              <summary className="hover:text-slate-600">Expected output</summary>
                              <p className="mt-1 p-2 bg-app text-slate-600 whitespace-pre-wrap rounded">{ex.expected_output}</p>
                            </details>
                          )}
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                )}

                {currentModule.cultural_spotlight && (
                  <TabsContent value="culture">
                    <div className="bg-white border border-slate-200 p-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">{currentModule.cultural_spotlight.topic}</h4>
                      <p className="text-xs text-slate-600 whitespace-pre-wrap mb-3">{currentModule.cultural_spotlight.content}</p>
                      {currentModule.cultural_spotlight.cultural_vocabulary?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {currentModule.cultural_spotlight.cultural_vocabulary.map((v, i) => (
                            <Badge key={i} className="text-[8px] px-1.5 py-0 bg-indigo-100 text-indigo-700 border-none">{v}</Badge>
                          ))}
                        </div>
                      )}
                      {currentModule.cultural_spotlight.real_life_application && (
                        <div className="p-3 bg-amber-50 border border-amber-200 text-[11px] text-amber-800 rounded">
                          <p className="font-medium mb-0.5">Real-life application:</p>
                          <p>{currentModule.cultural_spotlight.real_life_application}</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-4 border-t border-slate-200">
              <Button onClick={handlePrev}
                disabled={currentWeek === modules[0]?.week && currentLesson === 0}
                variant="outline" className="h-9 text-xs rounded-none gap-1">
                <ChevronLeft className="w-3.5 h-3.5" /> Previous
              </Button>
              <button onClick={handleMarkComplete}
                className={`flex items-center gap-1.5 h-9 px-4 text-xs font-medium ${
                  isCompleted
                    ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                    : "bg-emerald-600 hover:bg-emerald-500 text-white"
                }`}>
                {isCompleted ? <><CheckCircle className="w-4 h-4" /> Completed</> : <><Circle className="w-4 h-4" /> Mark Complete</>}
              </button>
              <Button onClick={handleNext}
                disabled={currentWeek === modules[modules.length - 1]?.week &&
                  currentLesson === ((modules[modules.length - 1]?.lessons || []).length - 1)}
                variant="outline" className="h-9 text-xs rounded-none gap-1">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        {sidebarOpen && (
          <div className="w-[380px] flex-shrink-0 border-l border-slate-200 overflow-y-auto bg-white">
            <CurriculumSidebar
              modules={modules}
              currentLecture={currentLesson != null ? `${currentWeek}-${currentLesson}` : null}
              currentWeek={currentWeek}
              onSelectLecture={handleSelectLecture}
              completedLectures={completedLectures}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────
// Course Overview Page (shown before any lecture)
// ──────────────────────────────────────────────────
function CourseOverview({ course, loading, modules, totalLectures, completedLectures, progressPct, onStart, onBack }) {
  if (loading || !course) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  const coverImage = course.cover_image || course.thumbnail
    || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=400&fit=crop";

  return (
    <div className="min-h-screen bg-app">
      {/* Cover Image Header */}
      <div className="relative h-48 sm:h-64 bg-slate-800 overflow-hidden">
        <img src={coverImage} alt={course.title}
          className="w-full h-full object-cover opacity-60"
          onError={(e) => { e.target.style.display = "none"; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
          <button onClick={onBack} className="text-white/70 hover:text-white text-xs mb-2 flex items-center gap-1">
            <ChevronLeft className="w-3.5 h-3.5" /> Back to Learning Hub
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">{course.title}</h1>
          <div className="flex items-center gap-3 text-xs text-white/80">
            <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {course.instructor || "AllBaise Learning"}</span>
            <span className="flex items-center gap-1"><BarChart3 className="w-3.5 h-3.5" /> {course.category}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {course.duration_hours || 0}h</span>
            <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {totalLectures} lessons</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6">
        {/* Progress */}
        {completedLectures.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span>Progress</span>
              <span>{completedLectures.length}/{totalLectures} ({progressPct}%)</span>
            </div>
            <Progress value={progressPct} className="h-2" />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Description + Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* About */}
            <div className="bg-white border border-slate-200 p-5">
              <h2 className="text-sm font-bold text-slate-800 mb-2">About This Course</h2>
              <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">{course.description}</p>
            </div>

            {/* Learning Objectives */}
            {course.learning_objectives && (
              <div className="bg-white border border-slate-200 p-5">
                <h2 className="text-sm font-bold text-slate-800 mb-3">Learning Objectives</h2>
                {Object.entries(course.learning_objectives).map(([level, objectives]) => (
                  <div key={level} className="mb-3 last:mb-0">
                    <h3 className="text-xs font-semibold text-amber-600 uppercase mb-1.5">{level.toUpperCase()} Level</h3>
                    <ul className="space-y-1">
                      {(objectives || []).map((obj, i) => (
                        <li key={i} className="text-[11px] text-slate-600 flex items-start gap-2">
                          <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {obj}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Course Stats */}
            <div className="bg-white border border-slate-200 p-5">
              <h2 className="text-sm font-bold text-slate-800 mb-3">Course Stats</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: "Total Sections", value: modules.length },
                  { label: "Total Lessons", value: totalLectures },
                  { label: "Duration", value: `${course.duration_hours || 0}h` },
                  { label: "Completed", value: `${completedLectures.length}/${totalLectures}` },
                ].map((s) => (
                  <div key={s.label} className="text-center p-3 bg-app">
                    <p className="text-lg font-bold text-slate-800">{s.value}</p>
                    <p className="text-[10px] text-slate-400">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Sidebar */}
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 p-5 text-center">
              <GraduationCap className="w-12 h-12 text-amber-500 mx-auto mb-2" />
              <h3 className="text-sm font-bold text-slate-800 mb-1">{course.title}</h3>
              <p className="text-[10px] text-slate-400 mb-4">{course.instructor || "AllBaise Learning"}</p>
              <Button onClick={onStart}
                className="w-full h-10 text-sm font-semibold rounded-none bg-amber-500 hover:bg-amber-600 gap-2">
                <PlayCircle className="w-4 h-4" />
                {completedLectures.length > 0 ? "Continue Learning" : "Start Course"}
              </Button>
            </div>

            {/* Curriculum preview */}
            <div className="bg-white border border-slate-200 p-5">
              <h3 className="text-xs font-bold text-slate-700 mb-3">Course Content ({modules.length} sections)</h3>
              <div className="space-y-1">
                {modules.slice(0, 8).map((mod) => {
                  const modCompleted = (mod.lessons || []).every((l) => completedLectures.includes(`${mod.week}-${l.title}`));
                  return (
                    <div key={mod.week} className="flex items-center gap-2 text-[10px] text-slate-500 py-0.5">
                      {modCompleted ? (
                        <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <Circle className="w-3 h-3 text-slate-300 flex-shrink-0" />
                      )}
                      <span className="truncate">Section {mod.week}: {mod.title}</span>
                    </div>
                  );
                })}
                {modules.length > 8 && (
                  <p className="text-[10px] text-slate-400 pt-1">+{modules.length - 8} more sections</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
